import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash, FaCheck } from "react-icons/fa";
import { API_BASE } from "../../config/apiConfig";
import { useToast } from "../../hooks/useToast";
import LoadingOverlay from "../../components/loadingOverlay";

export default function ResetPasswordPage() {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [confirmTouched, setConfirmTouched] = useState(false);

  const { showToast, ToastContainer } = useToast();
  const navigate = useNavigate();
  const email = useLocation().state?.email;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password || !confirm) return showToast("Please fill in all fields.", "error");
    if (password.length < 6) return showToast("Password must be at least 6 characters.", "error");
    if (password !== confirm) return showToast("Passwords do not match.", "error");
    if (!email) return showToast("Missing email context. Please restart the reset process.", "error");

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, new_password: password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Password reset failed.");

      // ✅ 2-second live countdown toast
      let countdown = 2;
      showToast(
        `Password reset successful!\nRedirecting to login in ${countdown}s...`,
        "success"
      );

      const interval = setInterval(() => {
        countdown--;
        showToast(
          `Password reset successful!\nRedirecting to login in ${countdown}s...`,
          "success",
          "top",
          true,
          2500
        );
        if (countdown <= 0) clearInterval(interval);
      }, 1000);

      setTimeout(() => {
        clearInterval(interval);
        navigate("/login");
      }, 2000);
    } catch (err) {
      showToast(err.message || "Password reset failed.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full flex-grow flex flex-col items-center justify-center py-16">
      <div className="bg-white rounded-xl shadow p-8 w-full max-w-md">
        <h1 className="text-2xl font-semibold text-center mb-6 text-gray-800">
          Reset Password
        </h1>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* New Password */}
          <div className="text-left relative">
            <label className="block text-sm font-medium mb-1">
              New Password <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showPw ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={() => setPasswordTouched(true)}
                placeholder="Enter new password"
                className={`placeholder:italic placeholder:text-gray-400 w-full border rounded-lg px-3 py-2 pr-10 ${
                  passwordTouched && password.length > 0 && password.length < 6
                    ? "border-red-500"
                    : ""
                }`}
              />
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                tabIndex={-1}
              >
                {showPw ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            {passwordTouched && password.length > 0 && password.length < 6 && (
              <p className="text-red-500 text-xs mt-1">
                Password must be at least 6 characters.
              </p>
            )}
          </div>

          {/* Confirm Password */}
          <div className="text-left relative">
            <label className="block text-sm font-medium mb-1">
              Confirm New Password <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showConfirmPw ? "text" : "password"}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                onBlur={() => setConfirmTouched(true)}
                placeholder="Confirm new password"
                className={`placeholder:italic placeholder:text-gray-400 w-full border rounded-lg px-3 py-2 pr-14 ${
                  confirmTouched &&
                  confirm &&
                  password !== confirm
                    ? "border-red-500"
                    : ""
                }`}
              />

              {/* ✅ Green check when match */}
              {password &&
                confirm &&
                password === confirm && (
                  <FaCheck className="absolute right-9 top-1/2 -translate-y-1/2 text-green-500" />
                )}

              {/* 👁 Toggle visibility */}
              <button
                type="button"
                onClick={() => setShowConfirmPw(!showConfirmPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                tabIndex={-1}
              >
                {showConfirmPw ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>

            {confirmTouched &&
              confirm &&
              password !== confirm && (
                <p className="text-red-500 text-xs mt-1">
                  Passwords do not match.
                </p>
              )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded disabled:opacity-60"
          >
            {loading ? "Resetting..." : "Reset Password"}
          </button>
        </form>
      </div>

      <LoadingOverlay show={loading} text="Loading..." />
      <ToastContainer />
    </div>
  );
}
